import React, { useCallback, useState } from 'react'
import type { JSX } from 'react'
import { Button, DatePicker, Row, Form, Input, Select } from 'antd'
import dayjs from 'dayjs'
import { useAppSelector } from '../../../app/hooks'
import sensors, { sensorsRole } from '../../../utils/sensors'
import DatesPickerComponent from '../../components/DatesPickerV2'
import DepartmentMemberPicker, {
  FilterObj
} from '../../components/DepartmentMemberPickerV2'
import { useAISecurity } from '../context/AISecurityContext'
import { RecognitionRecord, XCFilterParams } from '../types/contextInterface'
import type { Dayjs } from 'dayjs'
import SecurityEntryV2 from './SecurityEntryV2'
import { useSensorTracking } from '../hooks/useSensorTracking'
import CONFIG from '../../../constants/config'
import { DownOutlined, UpOutlined } from '@ant-design/icons'

// antd 中 RangePickerValue 类型定义
type RangePickerValue = [Dayjs | null, Dayjs | null] | null

const { RangePicker } = DatePicker
const FormItem = Form.Item
const Option = Select.Option
const DatesPicker = DatesPickerComponent as React.ComponentType<any>

type FilterModalType = 'time' | 'member' | 'problemType'

interface XCMemberChangeParams {
  selectedDepartmentUsers: any[]
  selectedDepartments: any[]
}

interface XCFilterState {
  showDatePickerPane: boolean
  showMemberPickerModal: boolean
  expanded: boolean // 添加展开/收起状态
}

interface FilterProps {
  dataSource: RecognitionRecord[]
  onSubmit?: () => void
  onReset?: () => void
}

/**
 * AI安全筛选组件
 * @param props - 组件属性
 */
const Filter: React.FC<FilterProps> = (props) => {
  const {
    dataSource,
    onSubmit: externalOnSubmit,
    onReset: externalOnReset
  } = props
  const { filterParams, setFilterParams, resetFilterParams, groupId, myRole } =
    useAISecurity()

  const { reportAISecurityPageActionClick, reportAISecurityClickFilter } =
    useSensorTracking()

  const pickerUserCount = useAppSelector(
    (state) => state.organization.pickerOrgUserCount
  )

  const questionTypeList = [
    '高处作业',
    '用电安全',
    '文明施工',
    '动火作业',
    '吊装作业',
    '设备隐患',
    '消防隐患'
  ]

  // 提交和重置处理函数

  const handleReset = useCallback(() => {
    // 上报重置埋点
    reportAISecurityPageActionClick({
      actionType: 'reset'
    })

    // 重置筛选参数
    resetFilterParams()

    // 调用外部传入的 onReset
    if (externalOnReset) {
      externalOnReset()
    }
  }, [
    resetFilterParams,
    externalOnReset,
    reportAISecurityPageActionClick,
    reportAISecurityClickFilter,
    filterParams
  ])

  const [state, setState] = useState<XCFilterState>({
    showDatePickerPane: false,
    showMemberPickerModal: false,
    expanded: true // 默认收起状态
  })

  const { showDatePickerPane, showMemberPickerModal, expanded } = state

  // 切换展开/收起状态
  const toggleExpand = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      expanded: !prevState.expanded
    }))
  }, [])

  /**
   * 处理日期变化
   * @param startDate - 开始日期
   * @param endDate - 结束日期
   */
  const handleDatesChange = useCallback(
    (startDate: Dayjs, endDate: Dayjs) => {
      setState((prevState) => ({ ...prevState, showDatePickerPane: false }))
      const newParams: XCFilterParams = {
        ...filterParams,
        startTime: startDate.valueOf(),
        endTime: endDate.valueOf()
      }
      setFilterParams(newParams)
    },
    [filterParams, setFilterParams]
  )

  const handleDatesCancel = useCallback(() => {
    setState((prevState) => ({ ...prevState, showDatePickerPane: false }))
  }, [])

  /**
   * 处理成员变化V2
   * @param params - 参数对象
   */
  const handleMemberChangeV2 = useCallback(
    (params: XCMemberChangeParams) => {
      const { selectedDepartmentUsers, selectedDepartments } = params
      setState((prevState) => ({ ...prevState, showMemberPickerModal: false }))

      // 上报成员筛选埋点
      reportAISecurityPageActionClick({
        actionType: 'choosePhotoMember'
      })

      // 保存完整用户对象，供 picker 回显用
      const newParams: XCFilterParams = {
        ...filterParams,
        departmentUsers: selectedDepartmentUsers,
        departments: selectedDepartments
      }
      setFilterParams(newParams)
    },
    [filterParams, setFilterParams, reportAISecurityPageActionClick]
  )

  /**
   * 时间选择
   * @param dates - 日期范围值
   * @param dateStrings - 日期字符串数组
   */
  const onChangeDate = (
    dates: RangePickerValue,
    dateStrings: [string, string]
  ) => {
    // 上报时间筛选埋点
    reportAISecurityPageActionClick({
      actionType: 'choosePhotoDate'
    })

    // 使用日期字符串而不是Moment对象，避免无效日期转换问题。
    // 起始日归到当天开始（00:00:00）、结束日归到当天结束（23:59:59），覆盖结束日一整天。
    // antd6 的 RangePicker 选中某天后时间部分是当天 00:00:00，若直接取 valueOf，
    // 选同一天筛选会让 startTime 与 endTime 都等于当天 00:00:00（区间为空），台账查不到记录。
    const newParams: XCFilterParams = {
      ...filterParams,
      startTime: dateStrings[0]
        ? Math.floor(dayjs(dateStrings[0]).startOf('day').valueOf() / 1000)
        : undefined,
      endTime: dateStrings[1]
        ? Math.floor(dayjs(dateStrings[1]).endOf('day').valueOf() / 1000)
        : undefined
    }

    setFilterParams(newParams)
  }

  /**
   * 处理筛选模态框
   * @param type - 模态框类型
   */
  const handleFilterModals = useCallback((type: FilterModalType) => {
    if (type === 'member') {
      setState((prevState) => ({
        ...prevState,
        showMemberPickerModal: !prevState.showMemberPickerModal,
        showDatePickerPane: false
      }))
    }
  }, [])

  /**
   * 处理提交按钮点击
   */
  const onClickSubmit = useCallback(() => {
    // 上报开始筛选埋点
    reportAISecurityPageActionClick({
      actionType: 'beginSearch'
    })

    // 上报筛选开始的详细埋点
    const trackingParams = {
      startTime: dayjs(filterParams.startTime).format(CONFIG.timeFormat),
      endTime: dayjs(filterParams.endTime).format(CONFIG.timeFormat),
      memberNum: filterParams.departmentUsers?.length || 0,
      isAllMember:
        !filterParams.departmentUsers ||
        filterParams.departmentUsers.length === 0,
      infoJson: JSON.stringify({
        keywords: filterParams.keywords || '',
        monomerName: filterParams.monomerName || '',
        questionTypeList: filterParams.questionTypeList || []
      }),
      actionType: 'searchBegin'
    }

    reportAISecurityClickFilter(trackingParams)

    // 调用外部传入的 onSubmit，并传递请求参数
    if (externalOnSubmit) {
      externalOnSubmit()
    }
  }, [
    filterParams,
    groupId,
    externalOnSubmit,
    reportAISecurityPageActionClick,
    reportAISecurityClickFilter
  ])

  /**
   * 计算成员选择的显示值
   * @returns 成员选择的显示值
   */
  const handleCalcMemberPickedValue = useCallback(() => {
    // 选中部门时，成员数需累加各部门人数（与照片/填表员等模块的计数口径一致），
    // 否则仅勾选部门会因 departmentUsers 为空而错误显示「已选0人」
    const userNumInDepartments = (filterParams.departments || [])
      .map((department: any) => department?.num || 0)
      .reduce((acc: number, cur: number) => acc + cur, 0)
    const pickedMembersNum =
      (filterParams.departmentUsers ? filterParams.departmentUsers.length : 0) +
      userNumInDepartments
    const hasSelection =
      filterParams.departmentUsers.length > 0 ||
      filterParams.departments.length > 0
    if (
      (!hasSelection && pickedMembersNum === 0) ||
      pickedMembersNum === pickerUserCount ||
      filterParams.departments.some((department: any) => department?.id === '0')
    ) {
      return '全部成员'
    }
    return `已选${pickedMembersNum}人`
  }, [filterParams.departmentUsers, filterParams.departments, pickerUserCount])

  // 检查开始时间是否晚于结束时间
  const isStartTimeAfterEndTime =
    filterParams.startTime && filterParams.endTime
      ? filterParams.startTime > filterParams.endTime
      : false

  return (
    // className="Filter filter-container"
    <div
      style={{
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        padding: '16px 20px',
        height: expanded ? 'auto' : '100px' // 收起时高度固定为60px
      }}
    >
      {/* 收起状态只显示一行 */}
      {!expanded ? (
        <Row style={{ display: 'flex', alignItems: 'center', height: '60px' }}>
          <Form
            layout="inline"
            style={{ display: 'flex', alignItems: 'center', flex: 1 }}
          >
            <FormItem label="隐患关键词" style={{ marginRight: '16px' }}>
              <Input
                style={{ width: '236px' }}
                placeholder="请输入关键词"
                maxLength={16}
                value={filterParams.keywords || ''}
                onChange={(e) => {
                  const newParams: XCFilterParams = {
                    ...filterParams,
                    keywords: e.target.value
                  }
                  setFilterParams(newParams)
                }}
              />
            </FormItem>
            <Button
              type="link"
              onClick={toggleExpand}
              style={{ marginRight: '16px' }}
            >
              更多筛选 <DownOutlined />
            </Button>
            <Button
              disabled={isStartTimeAfterEndTime}
              type="primary"
              style={{ marginRight: '10px' }}
              onClick={onClickSubmit}
            >
              开始筛选
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Form>
        </Row>
      ) : (
        <>
          <Row style={{ marginBottom: 8 }}>
            <Form layout="inline">
              <FormItem label="单体名称">
                <Input
                  style={{ width: '236px' }}
                  placeholder="请输入单体名称"
                  maxLength={16}
                  value={filterParams.monomerName || ''}
                  onChange={(e) => {
                    const newParams: XCFilterParams = {
                      ...filterParams,
                      monomerName: e.target.value
                    }
                    setFilterParams(newParams)
                  }}
                  onBlur={(e) => {
                    // 输入结束时上报单体名称搜索埋点
                    if (e.target.value.trim()) {
                      reportAISecurityPageActionClick({
                        actionType: 'searchMonomerName'
                      })
                    }
                  }}
                />
              </FormItem>
              <FormItem label="问题类型">
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '236px' }}
                  value={filterParams.questionTypeList || []}
                  placeholder="请选择问题类型"
                  onChange={(value: string[]) => {
                    // 上报问题类型搜索埋点
                    reportAISecurityPageActionClick({
                      actionType: 'searchProblemType'
                    })

                    const newParams: XCFilterParams = {
                      ...filterParams,
                      questionTypeList: value
                    }
                    setFilterParams(newParams)
                  }}
                >
                  {questionTypeList?.map((item) => {
                    return (
                      <Option key={item} value={item}>
                        {item ?? ''}
                      </Option>
                    )
                  })}
                </Select>
              </FormItem>
            </Form>
          </Row>
          <Row style={{ marginBottom: 8 }}>
            <Form layout="inline">
              <FormItem
                label="检查日期"
                validateStatus={isStartTimeAfterEndTime ? 'error' : ''}
                help={isStartTimeAfterEndTime ? '结束时间应晚于开始时间' : ''}
              >
                <RangePicker
                  value={
                    filterParams.startTime && filterParams.endTime
                      ? [
                          dayjs(filterParams.startTime * 1000),
                          dayjs(filterParams.endTime * 1000)
                        ]
                      : undefined
                  }
                  onChange={onChangeDate}
                  style={{ width: '236px' }}
                />
              </FormItem>
              <FormItem label="拍摄人员">
                <Select
                  style={{ width: '236px' }}
                  value={handleCalcMemberPickedValue()}
                  open={false}
                  onDropdownVisibleChange={() => {
                    handleFilterModals('member')
                  }}
                ></Select>
              </FormItem>
              <FormItem label="隐患关键词">
                <Input
                  style={{ width: '236px' }}
                  placeholder="请输入关键词"
                  maxLength={16}
                  value={filterParams.keywords || ''}
                  onBlur={(e) => {
                    // 上报关键词搜索埋点
                    if (e.target.value.trim()) {
                      reportAISecurityPageActionClick({
                        actionType: 'searchKeywords'
                      })
                    }
                  }}
                  onChange={(e) => {
                    const newParams: XCFilterParams = {
                      ...filterParams,
                      keywords: e.target.value
                    }
                    setFilterParams(newParams)
                  }}
                />
              </FormItem>
            </Form>
          </Row>
          <Row>
            <Form layout="inline">
              <FormItem label="" colon={false} style={{ marginLeft: '70px' }}>
                <Button
                  type="link"
                  onClick={toggleExpand}
                  style={{ marginRight: '16px' }}
                >
                  收起 <UpOutlined />
                </Button>
                <Button
                  disabled={isStartTimeAfterEndTime}
                  type="primary"
                  style={{ marginRight: '10px' }}
                  onClick={onClickSubmit}
                >
                  开始筛选
                </Button>
                <Button onClick={handleReset}>重置</Button>
              </FormItem>
            </Form>
          </Row>
        </>
      )}

      <DatesPicker
        visible={showDatePickerPane}
        dates={[
          filterParams.startTime ? dayjs(filterParams.startTime) : null,
          filterParams.endTime ? dayjs(filterParams.endTime) : null
        ]}
        maxMonths={1}
        onChange={handleDatesChange}
        onCancel={handleDatesCancel}
      />
      <DepartmentMemberPicker
        open={showMemberPickerModal}
        filterObj={{
          departmentUsers: filterParams.departmentUsers as any,
          departments: filterParams.departments
        }}
        onOk={handleMemberChangeV2}
        onCancel={() =>
          setState((prevState) => ({
            ...prevState,
            showMemberPickerModal: false
          }))
        }
      />
    </div>
  )
}

export default Filter
